import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { glassStyle } from "../data/utils";
import type { StateInfo, Subtopic } from "../types";
import { SOS_URLS, VPN_REQUIRED, EXTRA_LINKS } from "../data/lookups";
import type { EntityType } from "../canonical/model";
import type { CsrItem, CsrJurisdictionView } from "../canonical/csr";
import { unavailableServiceLabelsForPanel } from "../canonical/csr-alerts";
import {
  CORPORATION_UNAVAILABLE_TITLE,
  DEFAULT_ENTITY_TYPE,
} from "../canonical/selectability";
import { EntitySelector } from "./EntitySelector";

/** Safe default: LLC pickable, Corporation rendered but not selectable. */
const LLC_ONLY: readonly EntityType[] = ["llc"];

/** A panel mounted without a change handler still shows the selector. */
const NO_OP_ENTITY_CHANGE = (_next: EntityType): void => {};

type Props = {
  stateName: string;
  info: StateInfo;
  onClose: () => void;
  /**
   * The entity type whose content this panel shows. Threaded through now so the
   * panel is entity-aware ahead of corporation migration; it defaults to LLC and
   * currently changes nothing that is rendered.
   */
  entityType?: EntityType;
  onEntityTypeChange?: (next: EntityType) => void;
  /**
   * Entity types a CSR may actually pick.
   *
   * This does NOT control whether the selector is rendered — the selector is
   * permanent UI on every jurisdiction card. Entity types absent from this list
   * render disabled. Defaults to LLC only, which is the safe state; the caller
   * widens it per jurisdiction from whether a canonical record exists there.
   */
  enabledEntityTypes?: readonly EntityType[];
  /**
   * The canonical CSR topic view for this jurisdiction, when it has been
   * migrated.
   *
   * Present  → the panel renders the canonical topic model.
   * Absent   → the panel renders the legacy `StateInfo` fallback.
   *
   * This is what makes migration visible one batch at a time: a migrated state
   * shows the topics its authored data actually supports, and an unmigrated one
   * is untouched.
   */
  csrView?: CsrJurisdictionView;
};

// --- helpers --------------------------------------------------------------

function valueText(v: string | Subtopic[] | undefined): string {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.map((s) => s.label + " " + s.value).join(" ");
}

function isSubtopicArray(v: unknown): v is Subtopic[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    typeof (v as any)[0] === "object" &&
    "label" in (v as any)[0]
  );
}

// --- section catalog ------------------------------------------------------

/**
 * `typesOfFormation` is deliberately ABSENT from this union.
 *
 * The permanent LLC / Corporation selector above the grid answers the question
 * that topic used to answer, so the button is retired. The legacy data field
 * still exists — removing it would break legacy byte-equivalence, the
 * compatibility adapter and the unmigrated-state fallback — it simply has no
 * CSR-facing button any more.
 */
type SectionKey =
  | "formationFee"
  | "foreignFiling"
  | "managementStructure"
  | "purposeNaics"
  | "pob"
  | "certificateOfGoodStanding"
  | "renewalFee"
  | "renewalLateFee"
  | "renewalSchedule"
  | "reinstatement"
  | "dissolutionFee"
  | "nameAfterDissolution"
  | "amendmentFee"
  | "amendmentTypes"
  | "dba"
  | "dbaFee"
  | "dbaRenewalSchedule"
  | "certifiedCopies"
  | "apostille";

const SECTIONS: { key: SectionKey; label: string }[] = [
  // No "Types of Formation" entry: the entity selector replaces it.
  { key: "formationFee", label: "Formation Fee" },
  { key: "foreignFiling", label: "Foreign Filing" },
  { key: "managementStructure", label: "Management" },
  { key: "purposeNaics", label: "Purpose / NAICS" },
  { key: "pob", label: "Principal Office" },
  { key: "certificateOfGoodStanding", label: "Cert. of Good Standing" },
  { key: "renewalFee", label: "Renewal Fee" },
  { key: "renewalLateFee", label: "Renewal Late Fee" },
  { key: "renewalSchedule", label: "Renewal Schedule" },
  { key: "reinstatement", label: "Reinstatement" },
  { key: "dissolutionFee", label: "Dissolution Fee" },
  { key: "nameAfterDissolution", label: "Name After Diss." },
  { key: "amendmentFee", label: "Amendment Fee" },
  { key: "amendmentTypes", label: "Amendment Types" },
  { key: "dba", label: "DBA" },
  { key: "dbaFee", label: "DBA Fee" },
  { key: "dbaRenewalSchedule", label: "DBA Renewal" },
  { key: "certifiedCopies", label: "Certified Copies" },
  { key: "apostille", label: "Apostille" },
];

const REQUIREMENTS_KEY = "__requirements__";

/** A CSR item's searchable text, whichever half it carries. */
function csrItemText(item: CsrItem): string {
  return [item.label, item.text, item.guidance, item.detail].filter(Boolean).join(" ");
}

/** Short badge wording per CSR value state. "available" needs no badge. */
const CSR_STATE_BADGE: Record<string, string> = {
  needs_verification: "Verify first",
  unavailable: "Unavailable",
  not_applicable: "N/A",
};

// --- styles ---------------------------------------------------------------

const panelStyle: CSSProperties = {
  ...glassStyle,
  position: "fixed",
  bottom: 24,
  left: 24,
  width: 320,
  borderRadius: 18,
  padding: "16px 16px 14px",
  zIndex: 10,
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 12,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 19,
  fontWeight: 700,
  letterSpacing: 0.2,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const headerBtnsStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  flex: "0 0 auto",
};

const iconBtnStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#fff",
  padding: 5,
  cursor: "pointer",
  opacity: 0.75,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
};

const iconBtnActiveStyle: CSSProperties = {
  ...iconBtnStyle,
  background: "rgba(165,180,252,0.18)",
  opacity: 1,
};

const closeBtnStyle: CSSProperties = {
  ...iconBtnStyle,
  fontSize: 18,
  lineHeight: 1,
};

const searchWrapStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  marginBottom: 8,
  background: "rgba(0,0,0,0.35)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 12,
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  background: "transparent",
  border: "none",
  outline: "none",
  color: "#fff",
  fontSize: 13,
  fontFamily: "inherit",
  padding: 0,
  minWidth: 0,
};

const suggestionListStyle: CSSProperties = {
  marginBottom: 10,
  padding: 4,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 10,
  display: "flex",
  flexDirection: "column",
};

const suggestionItemStyle: CSSProperties = {
  textAlign: "left",
  background: "transparent",
  border: "none",
  color: "#fff",
  fontSize: 12,
  padding: "7px 10px",
  borderRadius: 6,
  cursor: "pointer",
  fontFamily: "inherit",
};

const gridScrollStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  paddingRight: 4,
  marginRight: -4,
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  alignContent: "start",
};

const gridBtnStyle: CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "#fff",
  padding: "11px 10px",
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.65,
  lineHeight: 1.25,
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
  transition: "background 0.15s, border-color 0.15s, transform 0.05s",
};

const gridBtnActiveStyle: CSSProperties = {
  background: "rgba(139,92,246,0.28)",
  borderColor: "rgba(165,180,252,0.55)",
};

const sosLinkBtnStyle: CSSProperties = {
  display: "block",
  marginTop: 12,
  padding: "10px 12px",
  background: "rgba(99,102,241,0.18)",
  border: "1px solid rgba(99,102,241,0.45)",
  color: "#c7d2fe",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  borderRadius: 10,
  textAlign: "center",
  textDecoration: "none",
};

const emptyStateStyle: CSSProperties = {
  fontSize: 12,
  opacity: 0.6,
  padding: "12px 4px",
  fontStyle: "italic",
  textAlign: "center",
};

// Container for the stack of detail cards: first card sits at the bottom of
// column 1, subsequent pins stack upward. When the column reaches the top of
// the screen, the next card starts a new column to the right (still bottom-up).
// Horizontal scroll kicks in only after columns overflow.
// pointerEvents: auto so the horizontal scrollbar is actually interactive
// (with pointerEvents: none the bar was visible but unclickable).
const cardStackStyle: CSSProperties = {
  position: "fixed",
  left: 24 + 320 + 12,
  top: 24,
  bottom: 70,
  right: 24,
  display: "flex",
  flexDirection: "column-reverse",
  flexWrap: "wrap",
  alignContent: "flex-start",
  gap: 10,
  overflowX: "auto",
  overflowY: "hidden",
  pointerEvents: "auto",
  zIndex: 11,
  paddingBottom: 4,
};

// Each detail card (instance — multiple can coexist).
const detailCardStyle: CSSProperties = {
  ...glassStyle,
  width: 280,
  maxHeight: 320,
  padding: "12px 14px 10px",
  borderRadius: 14,
  display: "flex",
  flexDirection: "column",
  pointerEvents: "auto",
  flex: "0 0 auto",
};

const detailHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 10,
  paddingBottom: 8,
  borderBottom: "1px solid rgba(255,255,255,0.10)",
};

const detailTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0.7,
  textTransform: "uppercase",
};

const pinBadgeStyle: CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "#c7d2fe",
  background: "rgba(99,102,241,0.20)",
  padding: "2px 7px",
  borderRadius: 999,
  border: "1px solid rgba(99,102,241,0.45)",
};

const detailScrollStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  paddingRight: 4,
};

const detailValueStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.55,
  opacity: 0.95,
  whiteSpace: "pre-line",
};

const subtopicListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const subtopicWrapStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

const subtopicLabelStyle: CSSProperties = {
  alignSelf: "flex-start",
  fontSize: 9.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  opacity: 0.75,
  marginBottom: 4,
  paddingBottom: 2,
  borderBottom: "1px solid rgba(255,255,255,0.15)",
};

const subtopicValueStyle: CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.55,
  opacity: 0.95,
  whiteSpace: "pre-line",
};

const reqListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 12.5,
  lineHeight: 1.55,
  opacity: 0.95,
};

// --- canonical CSR topic styles -------------------------------------------
// Same glass/neon language as the rest of the panel; no redesign.

const stripBaseStyle: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.45,
  padding: "6px 9px",
  borderRadius: 8,
  marginBottom: 8,
};

const discloseStripStyle: CSSProperties = {
  ...stripBaseStyle,
  background: "rgba(251,191,36,0.14)",
  border: "1px solid rgba(251,191,36,0.45)",
  color: "#fde68a",
};

const verifyStripStyle: CSSProperties = {
  ...stripBaseStyle,
  background: "rgba(96,165,250,0.12)",
  border: "1px solid rgba(147,197,253,0.42)",
  color: "#bfdbfe",
};

const unavailableStripStyle: CSSProperties = {
  ...stripBaseStyle,
  background: "rgba(148,163,184,0.14)",
  border: "1px solid rgba(203,213,225,0.38)",
  color: "#e2e8f0",
};

const csrQuestionStyle: CSSProperties = {
  fontSize: 11,
  opacity: 0.6,
  marginBottom: 8,
  fontStyle: "italic",
};

const csrRowStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  paddingBottom: 7,
  marginBottom: 7,
  borderBottom: "1px solid rgba(255,255,255,0.07)",
};

const csrRowLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  opacity: 0.62,
  fontWeight: 700,
};

const csrRowValueStyle: CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.5,
};

const csrBadgeStyle: CSSProperties = {
  display: "inline-block",
  fontSize: 9,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  padding: "1px 5px",
  borderRadius: 5,
  marginRight: 6,
  verticalAlign: "middle",
  background: "rgba(96,165,250,0.18)",
  border: "1px solid rgba(147,197,253,0.4)",
  color: "#bfdbfe",
};

const csrDiscloseBadgeStyle: CSSProperties = {
  ...csrBadgeStyle,
  background: "rgba(251,191,36,0.18)",
  borderColor: "rgba(251,191,36,0.5)",
  color: "#fde68a",
};

// --- component ------------------------------------------------------------

function MagnifierIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function StatePanel({
  stateName,
  info,
  onClose,
  entityType = DEFAULT_ENTITY_TYPE,
  onEntityTypeChange,
  enabledEntityTypes = LLC_ONLY,
  csrView,
}: Props) {
  const sosUrl = SOS_URLS[stateName];
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [pinnedKeys, setPinnedKeys] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset state on state change.
  useEffect(() => {
    setSearchOpen(false);
    setQuery("");
    setHoveredKey(null);
    setPinnedKeys(new Set());
  }, [stateName]);

  useEffect(() => {
    if (searchOpen) requestAnimationFrame(() => inputRef.current?.focus());
  }, [searchOpen]);

  const q = query.trim().toLowerCase();

  // --- canonical branch ---------------------------------------------------
  // A migrated jurisdiction renders its authored topics. Only topics whose data
  // actually exists are present in the view, so nothing empty is shown.
  const csrTopics = csrView?.topics ?? [];

  const visibleCsrTopics = useMemo(() => {
    if (!q) return csrTopics;
    return csrTopics.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.question.toLowerCase().includes(q) ||
        t.items.some((i) => csrItemText(i).toLowerCase().includes(q))
    );
  }, [q, csrTopics]);

  /** Safety-critical rows a CSR must not have to hunt for. */
  const csrAlerts = useMemo(() => {
    const disclose: string[] = [];
    const verify: string[] = [];
    const unavailable: string[] = [];
    for (const t of csrTopics) {
      if (t.items.some((i) => i.clientDisclosure)) disclose.push(t.label);
      if (t.items.some((i) => i.state === "needs_verification")) verify.push(t.label);
    }
    // What a CSR genuinely cannot sell here — empty for entity types that do not
    // show the strip at all. Both decisions live in `csr-alerts.ts`: which rows
    // count as unavailable SERVICES, and which entity types get the summary.
    // Keeping them there rather than here is what stops this component
    // accumulating product policy one condition at a time.
    unavailable.push(...unavailableServiceLabelsForPanel(entityType, csrTopics));
    return { disclose, verify, unavailable };
  }, [csrTopics, entityType]);

  const availableSections = useMemo(() => {
    return SECTIONS.filter(({ key }) => {
      const v = info[key];
      if (!v) return false;
      if (typeof v === "string") return v !== "—" && v !== "";
      return isSubtopicArray(v);
    });
  }, [info]);

  const hasRequirements =
    !!info.requirements && info.requirements.length > 0;

  const visibleSections = useMemo(() => {
    if (!q) return availableSections;
    return availableSections.filter(({ key, label }) => {
      const v = valueText(info[key] as any);
      return (
        label.toLowerCase().includes(q) || v.toLowerCase().includes(q)
      );
    });
  }, [q, availableSections, info]);

  const showRequirements =
    hasRequirements &&
    (!q ||
      "requirements".includes(q) ||
      info.requirements!.some((r) => r.toLowerCase().includes(q)));

  const suggestions = useMemo(() => {
    if (!q) return [] as { key: string; label: string }[];
    if (csrView) {
      return visibleCsrTopics
        .map((t) => ({ key: `topic:${t.topicId}`, label: t.label }))
        .slice(0, 6);
    }
    const out: { key: string; label: string }[] = [];
    for (const { key, label } of availableSections) {
      if (label.toLowerCase().includes(q)) out.push({ key, label });
    }
    for (const { key, label } of availableSections) {
      if (out.find((o) => o.key === key)) continue;
      const v = valueText(info[key] as any);
      if (v.toLowerCase().includes(q)) out.push({ key, label });
    }
    if (showRequirements) {
      out.push({ key: REQUIREMENTS_KEY, label: "Requirements" });
    }
    return out.slice(0, 6);
  }, [q, availableSections, info, showRequirements, csrView, visibleCsrTopics]);

  const nothingMatches = csrView
    ? !!q && visibleCsrTopics.length === 0
    : !!q && visibleSections.length === 0 && !showRequirements;

  function closeSearch() {
    setSearchOpen(false);
    setQuery("");
  }

  function pickSuggestion(s: { key: string; label: string }) {
    setPinnedKeys((prev) => {
      const next = new Set(prev);
      next.add(s.key);
      return next;
    });
    setQuery("");
    setSearchOpen(false);
  }

  function togglePin(key: string) {
    setPinnedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function unpin(key: string) {
    setPinnedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  function onButtonClick(key: string) {
    togglePin(key);
  }

  function onButtonHover(key: string | null) {
    setHoveredKey(key);
  }

  // Build the list of buttons to render (filtered).
  type Btn = { key: string; label: string };
  const buttons: Btn[] = csrView
    ? visibleCsrTopics.map((t) => ({ key: `topic:${t.topicId}`, label: t.label }))
    : [
        ...visibleSections.map(({ key, label }) => ({ key, label })),
        ...(showRequirements ? [{ key: REQUIREMENTS_KEY, label: "Requirements" }] : []),
      ];

  // Look up label/value for any key.
  function getSectionData(
    key: string
  ): { label: string; value: string | Subtopic[] | string[] } | null {
    if (key === REQUIREMENTS_KEY && hasRequirements) {
      return { label: "Requirements", value: info.requirements! };
    }
    const sec = SECTIONS.find((s) => s.key === key);
    if (!sec) return null;
    const v = info[sec.key] as string | Subtopic[] | undefined;
    if (v == null) return null;
    return { label: sec.label, value: v };
  }

  // Visible cards: every pinned key + a hover preview if hovering a non-pinned button.
  const visibleCardKeys: { key: string; pinned: boolean }[] = [];
  for (const k of pinnedKeys) visibleCardKeys.push({ key: k, pinned: true });
  if (hoveredKey && !pinnedKeys.has(hoveredKey)) {
    visibleCardKeys.push({ key: hoveredKey, pinned: false });
  }

  return (
    <>
      <div className="state-panel" style={panelStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>{stateName}</h2>
          <div style={headerBtnsStyle}>
            <button
              onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
              aria-label={searchOpen ? "Close search" : "Search"}
              style={searchOpen ? iconBtnActiveStyle : iconBtnStyle}
            >
              <MagnifierIcon />
            </button>
            <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>
              ✕
            </button>
          </div>
        </div>

        {/*
          Permanent UI: directly beneath the state name/header row and above the
          topic-button grid. Rendered unconditionally and on purpose — it is not
          gated on how many entity records exist or on global migration
          progress. Only which buttons are ENABLED varies, and that comes from
          whether this jurisdiction has a record of that entity type to open.
        */}
        <EntitySelector
          value={entityType}
          onChange={onEntityTypeChange ?? NO_OP_ENTITY_CHANGE}
          enabledOptions={enabledEntityTypes}
          disabledTitle={CORPORATION_UNAVAILABLE_TITLE}
        />

        {/*
          Safety strip. A client disclosure and an unresolved item are the two
          things a CSR must never miss, so they are named here rather than left
          to be discovered by clicking through topics.
        */}
        {csrAlerts.disclose.length > 0 ? (
          <div style={discloseStripStyle}>
            <strong>Tell the client before purchase:</strong>{" "}
            {csrAlerts.disclose.join(", ")}
          </div>
        ) : null}
        {csrAlerts.verify.length > 0 ? (
          <div style={verifyStripStyle}>
            <strong>Verify before advising:</strong> {csrAlerts.verify.join(", ")}
          </div>
        ) : null}
        {csrAlerts.unavailable.length > 0 ? (
          <div style={unavailableStripStyle}>
            <strong>Cannot be sold here:</strong> {csrAlerts.unavailable.join(", ")}
          </div>
        ) : null}

        {searchOpen ? (
          <>
            <div style={searchWrapStyle}>
              <span style={{ opacity: 0.7, display: "inline-flex" }}>
                <MagnifierIcon />
              </span>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeSearch();
                }}
                placeholder="Search this state…"
                style={searchInputStyle}
              />
              {query ? (
                <button
                  onClick={() => {
                    setQuery("");
                    inputRef.current?.focus();
                  }}
                  aria-label="Clear"
                  style={{ ...iconBtnStyle, fontSize: 12, opacity: 0.6, padding: 2 }}
                >
                  ✕
                </button>
              ) : null}
            </div>

            {suggestions.length > 0 ? (
              <div style={suggestionListStyle}>
                {suggestions.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => pickSuggestion(s)}
                    style={suggestionItemStyle}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.background =
                        "rgba(165,180,252,0.18)")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.background =
                        "transparent")
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        <div className="panel-scroll" style={gridScrollStyle}>
          {nothingMatches ? (
            <div style={emptyStateStyle}>No matches for "{query}".</div>
          ) : (
            <div style={gridStyle}>
              {buttons.map(({ key, label }) => {
                const active =
                  pinnedKeys.has(key) || hoveredKey === key;
                return (
                  <button
                    key={key}
                    onMouseEnter={() => onButtonHover(key)}
                    onMouseLeave={() => onButtonHover(null)}
                    onClick={() => onButtonClick(key)}
                    style={{
                      ...gridBtnStyle,
                      ...(active ? gridBtnActiveStyle : null),
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {sosUrl ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
              }}
            >
              <a
                href={sosUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...sosLinkBtnStyle, marginTop: 0, flex: 1 }}
              >
                Secretary of State Business Search ↗
              </a>
              {VPN_REQUIRED[stateName] ? <VpnBadge /> : null}
            </div>
          ) : null}

          {EXTRA_LINKS[stateName]?.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...sosLinkBtnStyle, marginTop: 8 }}
            >
              {link.label} ↗
            </a>
          ))}
        </div>
      </div>

      {visibleCardKeys.length > 0 ? (
        <div className="card-stack" style={cardStackStyle}>
          {visibleCardKeys.map(({ key, pinned }) => {
            if (key.startsWith("topic:")) {
              const topicId = key.slice("topic:".length);
              const topic = csrTopics.find((t) => t.topicId === topicId);
              if (!topic) return null;
              return (
                <CsrTopicCard
                  key={key}
                  label={topic.label}
                  question={topic.question}
                  items={topic.items}
                  pinned={pinned}
                  onUnpin={() => unpin(key)}
                />
              );
            }
            const data = getSectionData(key);
            if (!data) return null;
            return (
              <DetailCard
                key={key}
                label={data.label}
                value={data.value}
                pinned={pinned}
                onUnpin={() => unpin(key)}
              />
            );
          })}
        </div>
      ) : null}
    </>
  );
}

function DetailCard({
  label,
  value,
  pinned,
  onUnpin,
}: {
  label: string;
  value: string | Subtopic[] | string[];
  pinned: boolean;
  onUnpin: () => void;
}) {
  return (
    <div style={detailCardStyle}>
      <div style={detailHeaderStyle}>
        <h3 style={detailTitleStyle}>{label}</h3>
        {pinned ? (
          <button
            onClick={onUnpin}
            aria-label="Unpin"
            style={{ ...iconBtnStyle, padding: "2px 8px" }}
            title="Unpin"
          >
            <span style={pinBadgeStyle}>Pinned ✕</span>
          </button>
        ) : null}
      </div>
      <div style={detailScrollStyle}>
        <DetailBody value={value} />
      </div>
    </div>
  );
}

/**
 * One canonical CSR topic.
 *
 * Deliberately dumb: every decision about what a CSR may see — whether an item
 * is an answer, what to say when it is not, whether it is a client disclosure —
 * was made in the canonical projection. This renders what it is given and
 * invents nothing.
 */
function CsrTopicCard({
  label,
  question,
  items,
  pinned,
  onUnpin,
}: {
  label: string;
  question: string;
  items: CsrItem[];
  pinned: boolean;
  onUnpin: () => void;
}) {
  return (
    <div style={detailCardStyle}>
      <div style={detailHeaderStyle}>
        <h3 style={detailTitleStyle}>{label}</h3>
        {pinned ? (
          <button
            onClick={onUnpin}
            aria-label="Unpin"
            style={{ ...iconBtnStyle, padding: "2px 8px" }}
            title="Unpin"
          >
            <span style={pinBadgeStyle}>Pinned ✕</span>
          </button>
        ) : null}
      </div>
      <div style={detailScrollStyle}>
        <div style={csrQuestionStyle}>{question}</div>
        {items.map((item, i) => {
          const badge = CSR_STATE_BADGE[item.state];
          return (
            <div
              key={item.id}
              style={i === items.length - 1 ? { ...csrRowStyle, borderBottom: "none" } : csrRowStyle}
            >
              <span style={csrRowLabelStyle}>{item.label}</span>
              <span style={csrRowValueStyle}>
                {item.clientDisclosure ? (
                  <span style={csrDiscloseBadgeStyle}>Tell the client</span>
                ) : null}
                {badge ? <span style={csrBadgeStyle}>{badge}</span> : null}
                {item.text ?? item.guidance}
                {item.detail ? <NoteAsterisk note={item.detail} /> : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailBody({ value }: { value: string | Subtopic[] | string[] }) {
  // Requirements case
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "string") {
    return (
      <ul style={reqListStyle}>
        {(value as string[]).map((r, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{r}</li>
        ))}
      </ul>
    );
  }
  // Subtopic array case
  if (Array.isArray(value) && isSubtopicArray(value)) {
    return (
      <div style={subtopicListStyle}>
        {value.map((sub, i) => (
          <div key={i} style={subtopicWrapStyle}>
            <span style={subtopicLabelStyle}>{sub.label}</span>
            <span style={subtopicValueStyle}>
              {sub.value}
              {sub.note ? <NoteAsterisk note={sub.note} /> : null}
            </span>
          </div>
        ))}
      </div>
    );
  }
  // Plain string case
  return <div style={detailValueStyle}>{value as string}</div>;
}

const asteriskBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: 6,
  width: 16,
  height: 16,
  background: "transparent",
  border: "1px solid rgba(165,180,252,0.55)",
  color: "#c7d2fe",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  cursor: "help",
  fontFamily: "inherit",
  padding: 0,
};

const noteTipStyle: CSSProperties = {
  position: "fixed",
  width: 240,
  zIndex: 200,
  background: "rgba(8,10,18,0.94)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12,
  padding: "10px 12px",
  boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  color: "#fff",
  fontSize: 12,
  lineHeight: 1.5,
  pointerEvents: "none",
};

function NoteAsterisk({ note }: { note: string }) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);

  function open() {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const W = 240;
    let left = r.right + 8;
    if (left + W + 8 > window.innerWidth) left = r.left - W - 8;
    if (left < 8) left = 8;
    const top = Math.max(8, r.top - 8);
    setTip({ left, top });
  }
  function close() {
    setTip(null);
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        aria-label="More info"
        style={asteriskBtnStyle}
      >
        *
      </button>
      {tip
        ? createPortal(
            <div style={{ ...noteTipStyle, left: tip.left, top: tip.top }}>
              {note}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

// ---------- VPN required badge ------------------------------------------

const vpnBadgeBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  padding: 0,
  background: "rgba(37,99,235,0.18)",
  border: "1px solid rgba(96,165,250,0.55)",
  borderRadius: 8,
  cursor: "help",
  flex: "0 0 auto",
};

function VpnShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 28"
      width="18"
      height="20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id="vpnShieldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
      </defs>
      <path
        d="M12 1 L22 5 L22 13 C22 19.5 17.5 24.5 12 27 C6.5 24.5 2 19.5 2 13 L2 5 Z"
        fill="url(#vpnShieldGrad)"
        stroke="#93c5fd"
        strokeWidth="0.6"
      />
      <text
        x="12"
        y="17"
        fontFamily="sans-serif"
        fontSize="6.5"
        fontWeight="800"
        fill="#ffffff"
        textAnchor="middle"
        letterSpacing="0.3"
      >
        VPN
      </text>
    </svg>
  );
}

function VpnBadge() {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);

  function open() {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const W = 220;
    let left = r.right + 8;
    if (left + W + 8 > window.innerWidth) left = r.left - W - 8;
    if (left < 8) left = 8;
    const top = Math.max(8, r.top - 8);
    setTip({ left, top });
  }
  function close() {
    setTip(null);
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        tabIndex={0}
        aria-label="VPN required"
        style={vpnBadgeBtnStyle}
      >
        <VpnShieldIcon />
      </span>
      {tip
        ? createPortal(
            <div
              style={{
                ...noteTipStyle,
                left: tip.left,
                top: tip.top,
                width: 220,
              }}
            >
              VPN required — connect to a VPN before opening this state's
              business search portal.
            </div>,
            document.body
          )
        : null}
    </>
  );
}
